/*
  Free-shipping message + progress bar for the Kaching cart drawer.

  Renders into a merchant-placed element `#rl-free-shipping-bar` (add it via a
  Kaching custom-HTML block or anywhere in the drawer). Logic:

    - If ANY cart line has a selling plan (subscription)  -> free shipping (unlocked).
    - Otherwise (all one-time)                            -> progress toward the
      threshold, based on the cart total AFTER discounts.

  Config comes from theme settings, exposed on window.RLFreeShipping (see theme.liquid):
    enabled, threshold (£, integer), msgSubscription, msgUnlocked, msgProgress
    ([amount] token), moneyFormat (shop.money_format, used for the currency symbol).

  Cart data is NOT fetched here. The React header (webpack/src/components/header)
  owns a single /cart.js fetch, publishes it on window.cartData, and broadcasts a
  `cart:data` event (detail = { item_count, total_price, currency, has_subscription }
  | null). This bar just consumes that: `total_price` is discount-aware and fresh,
  `has_subscription` covers the subscription rule. detail === null (cart unknown /
  fetch failed) hides the bar rather than showing a stale/guessed number.

  Markup reuses Kaching's own `kaching-cart__tiered-promotions-bar*` classes so it
  inherits the app's styling. A content signature prevents redundant writes.
*/
(function () {
  var cfg = window.RLFreeShipping || {};
  if (cfg.enabled != true) return; // disabled via theme settings

  var TARGET_ID = cfg.targetId || "rl-free-shipping-bar";
  // Default to £40 only when unset — a merchant-set 0 (free shipping for all) must survive.
  var thresholdPounds = cfg.threshold == null || cfg.threshold === "" ? 40 : Number(cfg.threshold);
  if (!isFinite(thresholdPounds) || thresholdPounds < 0) thresholdPounds = 40;
  var thresholdPence = Math.round(thresholdPounds * 100);

  function fmt(pence, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency", currency: currency || "GBP",
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(pence / 100);
    } catch (e) {
      // Fallback: use the store's money format (window.RLFreeShipping.moneyFormat),
      // substituting the amount (2 decimals) into Shopify's {{amount}} placeholder.
      var amount = (pence / 100).toLocaleString("en", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
      var f = (cfg.moneyFormat || "").replace(/<[^>]+>/g, ""); // strip any markup
      return f ? f.replace(/\{\{\s*amount[^}]*\}\}/, amount) : amount;
    }
  }

  // Render the bar for a given total (pence) + subscription flag into the (visible) el.
  function render(el, totalPence, hasSubscription, currency) {
    // Messages are merchant-authored HTML from theme settings (trusted) and render
    // as-is, so bold/other tags in the copy work. [amount] = remaining sum.
    var unlocked, percent, messageHtml;
    if (hasSubscription) {
      unlocked = true;
      percent = 100;
      messageHtml = cfg.msgSubscription || "<b>Free subscription delivery</b> unlocked!";
    } else if (totalPence >= thresholdPence) {
      unlocked = true;
      percent = 100;
      messageHtml = cfg.msgUnlocked || "<b>Free delivery</b> unlocked!";
    } else {
      unlocked = false;
      percent = thresholdPence > 0 ? Math.min(100, Math.round((totalPence / thresholdPence) * 100)) : 0;
      var remaining = Math.max(0, thresholdPence - totalPence);
      messageHtml = (cfg.msgProgress || "Add <b>[amount]</b> to unlock <b>Free delivery</b>!")
        .replace("[amount]", fmt(remaining, currency));
    }

    // Skip if nothing changed — avoids redundant writes.
    var sig = (unlocked ? "1" : "0") + "|" + percent + "|" + messageHtml;
    if (el.getAttribute("data-fs-sig") === sig) return;

    el.innerHTML =
      '<div class="kaching-cart__tiered-promotions-bar">' +
        '<p class="kaching-cart__tiered-promotions-bar-text">' + messageHtml + "</p>" +
        '<div class="kaching-cart__tiered-promotions-bar-progress-container" dir="ltr">' +
          '<div class="kaching-cart__tiered-promotions-bar-progress-fill" dir="ltr" data-progress-percentage="' +
            percent + '" style="width:' + percent + '%">&nbsp;</div>' +
        "</div>" +
      "</div>";
    el.setAttribute("data-fs-sig", sig);
    el.setAttribute("data-fs-state", unlocked ? "unlocked" : "progress");
  }

  // Apply a cart:data payload: render from it, or hide when the cart is unknown.
  function apply(data) {
    var el = document.getElementById(TARGET_ID);
    if (!el) return;
    if (!data || typeof data.total_price !== "number") {
      el.style.display = "none";
      el.removeAttribute("data-fs-sig");
      return;
    }
    el.style.display = "";
    render(el, data.total_price, !!data.has_subscription, data.currency || "GBP");
  }

  document.addEventListener("cart:data", function (e) { apply(e.detail); });
  // Kaching mounts the drawer's custom blocks empty and the header doesn't fetch on
  // open, so paint from the current shared snapshot when the drawer opens. The bar
  // element already exists when kaching-cart:open fires, so no retry/observer is needed.
  document.addEventListener("kaching-cart:open", function () { apply(window.cartData); });
  // In case the header published before this script ran (undefined = not yet published).
  if (window.cartData !== undefined) apply(window.cartData);
})();
